import type { VideoModel } from '..';
import {
    BaseVideoProvider,
    type VideoProviderAdapter,
    TaskStatus,
    type CreateTaskConfig,
    type CreateTaskResult,
    type PollTaskConfig,
    type PollTaskResult,
} from '../base';
import {
    buildInputPayloadFromConfig,
    getTencentModelConfig,
    getTencentCredentials,
    getTencentSubAppId,
} from './utils';
import type { TencentCreateResponse, TencentTaskResponse } from './types';
import { buildTencentCloudHeaders } from './signature';
import { getValueByPath, isStatusMatch } from '../tools';
import { applyTencentMaterialReferences } from './material-reference';

// 腾讯云点播 (VOD) AIGC 生视频公共参数
const TENCENT_VOD_SERVICE = 'vod';
const TENCENT_VOD_VERSION = '2018-07-17';
const CREATE_ACTION = 'CreateAigcVideoTask';
const POLL_ACTION = 'DescribeTaskDetail';

/**
 * 构建带 v3 签名的腾讯云 API 请求配置
 * 注意：签名基于 payload 字符串计算，body 必须原样发送
 */
function buildSignedRequest(baseUrl: string, action: string, payload: Record<string, any>): {
    url: string;
    headers: Record<string, string>;
    body: string;
} {
    const { secretId, secretKey } = getTencentCredentials();
    const host = new URL(baseUrl).host;
    const body = JSON.stringify(payload);
    const headers = buildTencentCloudHeaders({
        secretId: secretId,
        secretKey: secretKey,
        host: host,
        service: TENCENT_VOD_SERVICE,
        action: action,
        version: TENCENT_VOD_VERSION,
        payload: body,
    });
    return {
        url: `${baseUrl}/`,
        headers: headers,
        body: body,
    };
}

// 创建提供商实例
const provider = new BaseVideoProvider({
    baseUrlEnv: 'TENCENT_BASE_URL',
    // 腾讯云使用 AKSK 签名鉴权，此处仅用于满足基类的非空校验
    apiKeyEnv: 'TENCENT_CLOUD_SECRET_ID',
    debugEnv: 'TENCENT_DEBUG',
    providerName: 'tencent',
});

// 实现适配器
const adapter: VideoProviderAdapter = {
    // 构建**任务创建**请求
    async buildCreateTaskRequest(modelId, params, context): Promise<CreateTaskConfig> {
        // 构建输入参数（可能是异步构建）
        const rawInput = await buildInputPayloadFromConfig(modelId, params);
        // Inngest 自动审核完成后会注入 URL -> AssetId 映射；腾讯任务使用 asset:// 引用。
        const input = applyTencentMaterialReferences(rawInput, params.providerOptions);
        // 补充点播应用 ID
        const payload = {
            SubAppId: getTencentSubAppId(),
            ...input,
        };
        // 计算 v3 签名并返回请求配置
        const signed = buildSignedRequest(context.baseUrl, CREATE_ACTION, payload);
        return {
            url: signed.url,
            method: 'POST',
            headers: signed.headers,
            body: signed.body,
        };
    },

    // 解析**任务创建**响应
    parseCreateTaskResponse(modelId, response, data: TencentCreateResponse): CreateTaskResult {
        // 腾讯云业务错误也返回 HTTP 200，错误在 Response.Error 中
        const error = data?.Response?.Error;
        if (error) {
            throw new Error(`${error.Code}: ${error.Message}`);
        }
        // 获取服务商配置
        const config = getTencentModelConfig(modelId);
        const taskIdPath = config.statusMapping.taskIdPath;
        // 获取任务 ID
        const taskId = getValueByPath(data, taskIdPath);
        if (!taskId) {
            throw new Error(`Failed to get task id from response`);
        }
        // 返回任务 ID
        return {
            taskId: taskId,
            raw: data,
        };
    },

    // 构建**轮询任务**请求
    buildPollTaskRequest(modelId, taskId, params, context): PollTaskConfig {
        // DescribeTaskDetail 使用 POST + 请求体传递 TaskId
        const payload = {
            TaskId: taskId,
            SubAppId: getTencentSubAppId(),
        };
        // 计算 v3 签名并返回请求配置
        const signed = buildSignedRequest(context.baseUrl, POLL_ACTION, payload);
        return {
            url: signed.url,
            method: 'POST',
            headers: signed.headers,
            body: signed.body,
        };
    },

    // 解析**轮询任务**响应
    parsePollTaskResponse(modelId, response, data: TencentTaskResponse): PollTaskResult {
        // 读取服务商配置 (状态映射)
        const config = getTencentModelConfig(modelId);
        const statusMapping = config.statusMapping;

        // 腾讯云业务错误也返回 HTTP 200，错误在 Response.Error 中
        const apiError = data?.Response?.Error;
        if (apiError) {
            return {
                status: TaskStatus.FAILED,
                error: `${apiError.Code}: ${apiError.Message}`,
                raw: data,
            };
        }

        // 获取任务状态
        const state = getValueByPath(data, statusMapping.statePath);
        // 如果状态值未找到则视为**处理中**
        if (state === undefined) {
            return {
                status: TaskStatus.PROCESSING,
                raw: data,
            };
        }

        // 1. 判断**完成**状态 (FINISH 包含成功与失败，需结合 ErrCode 判断)
        if (isStatusMatch(state, statusMapping.success)) {
            // ErrCode 非 0 表示任务失败
            const errCode = getValueByPath(data, statusMapping.errCodePath);
            if (typeof errCode === 'number' && errCode !== 0) {
                const error = getValueByPath(data, statusMapping.errorPath);
                return {
                    status: TaskStatus.FAILED,
                    error: String(error || `ErrCode: ${errCode}`),
                    raw: data,
                };
            }
            // 获取视频 URL
            const videoUrl = getValueByPath(data, statusMapping.videoUrlPath);
            // 视频 URL 不存在则视为**失败**状态
            if (!videoUrl) {
                return {
                    status: TaskStatus.FAILED,
                    error: 'Missing video URL in result',
                    raw: data,
                };
            }
            // 返回**成功**状态
            return {
                status: TaskStatus.SUCCESS,
                videoUrl: videoUrl,
                raw: data,
            };
        }

        // 2. 判断**失败**状态
        if (isStatusMatch(state, statusMapping.failed)) {
            const error = getValueByPath(data, statusMapping.errorPath);
            // 返回**失败**状态
            return {
                status: TaskStatus.FAILED,
                error: String(error) || 'unknown error',
                raw: data,
            };
        }

        // 3. 判断**处理中**状态
        if (isStatusMatch(state, statusMapping.processing)) {
            // 返回**处理中**状态
            return {
                status: TaskStatus.PROCESSING,
                raw: data,
            };
        }

        // 4. 判断**未知**状态 (不在配置范围内)
        // 返回**处理中**状态
        return {
            status: TaskStatus.PROCESSING,
            raw: data,
        };
    },
};

// 导出工厂函数
export const tencent = (modelId: string): VideoModel => {
    const config = getTencentModelConfig(modelId);
    return provider.createVideoModel(modelId, adapter);
};
